import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import {
  coerceToDate,
  dateFieldFormatError,
  dateFieldPlaceholder,
  dateFieldRangeError,
  DateFieldStorage,
  formatDateField,
  parseTypedDate,
  serializeDateField,
} from '../../utils/date-time-input';

/**
 * Text entry plus the product date / date-time popup.
 * Date fields use a calendar only. Date-time fields also show the time columns.
 * Typed values accept DD-MMM-YYYY, DD/MM/YYYY, and DD-MM-YYYY, then display as DD-MMM-YYYY.
 */
@Component({
  selector: 'app-date-time-field',
  templateUrl: './date-time-field.component.html',
  styleUrls: ['./date-time-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimeFieldComponent),
      multi: true,
    },
  ],
})
export class DateTimeFieldComponent implements ControlValueAccessor, Validator, OnChanges, OnInit, OnDestroy {
  /** Live instances, including fields inside ion-modal overlays that ViewChildren may miss. */
  private static readonly activeFields = new Set<DateTimeFieldComponent>();

  static getActiveFields(): DateTimeFieldComponent[] {
    return Array.from(DateTimeFieldComponent.activeFields);
  }

  /** Calendar only. Stored value is YYYY-MM-DD. */
  @Input() dateOnly = false;
  /** How a date-time value is written back. Date-only fields always use YYYY-MM-DD. */
  @Input() storage: DateFieldStorage = 'offset';
  @Input() disabled = false;
  @Input() required = false;
  @Input() min: string | Date | null = null;
  @Input() max: string | Date | null = null;
  @Input() requiredMessage = 'This field is required.';
  /** Popup opens from this element when set, so it stays on the date box that was clicked. */
  @Input() popupAnchor?: HTMLElement;
  /** Opens the popup when the parent sets this true, for example from a "Set date" button. */
  @Input()
  set autoOpen(value: boolean) {
    if (value && !this.autoOpenSeen && !this.disabled) {
      this.pickerOpen = true;
    }
    this.autoOpenSeen = !!value;
  }

  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() dismissed = new EventEmitter<void>();
  /** Parent handles confirm-before-clear. When unused, the icon clears the value itself. */
  @Output() clearRequest = new EventEmitter<void>();

  text = '';
  error = '';
  pickerOpen = false;
  touched = false;

  private stored: string | null = null;
  private focused = false;
  private publishing = false;
  private autoOpenSeen = false;
  private typingGeneration = 0;
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};

  get placeholder(): string {
    return dateFieldPlaceholder(this.dateOnly);
  }

  get pickerValue(): string | null {
    return this.stored;
  }

  ngOnInit(): void {
    DateTimeFieldComponent.activeFields.add(this);
  }

  ngOnDestroy(): void {
    DateTimeFieldComponent.activeFields.delete(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] || changes['min'] || changes['max'] || changes['dateOnly']) {
      this.onValidatorChange();
    }
  }

  writeValue(value: string | Date | null): void {
    if (this.publishing) {
      return;
    }
    const date = coerceToDate(value);
    this.stored = date ? serializeDateField(date, this.dateOnly, this.storage) : null;
    if (!date) {
      this.text = '';
      this.error = this.required && this.touched ? this.requiredMessage : '';
      this.focused = false;
      return;
    }
    if (this.focused) {
      return;
    }
    this.text = formatDateField(date, this.dateOnly);
    this.error = '';
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.pickerOpen = false;
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  validate(): ValidationErrors | null {
    const typed = (this.text || '').trim();
    if (!typed) {
      return this.required ? { required: true } : null;
    }
    if (!this.stored || this.error) {
      return { invalidDate: true };
    }
    return null;
  }

  onFocus(): void {
    this.focused = true;
  }

  onTyping(event: Event): void {
    const generation = ++this.typingGeneration;
    const host = event.target as { getInputElement?: () => Promise<HTMLInputElement> };
    const getInputElement = host.getInputElement;
    if (!getInputElement) {
      return;
    }
    void this.normalizeCompletedText(getInputElement.bind(host), generation);
  }

  /**
   * Reformat only after a finished value typed at the end of the field.
   * Editing the day at the start must leave the caret where the user is typing.
   */
  private async normalizeCompletedText(
    getInputElement: () => Promise<HTMLInputElement>,
    generation: number,
  ): Promise<void> {
    const native = await getInputElement();
    if (generation !== this.typingGeneration) {
      return;
    }
    const value = native.value || '';
    const raw = value.trim();
    if (!raw) {
      this.error = '';
      return;
    }
    const caretAtEnd = native.selectionStart === value.length && native.selectionEnd === value.length;
    if (!caretAtEnd) {
      return;
    }
    if (!this.dateOnly && !/\d{1,2}:\d{2}/.test(raw)) {
      return;
    }
    const parsed = parseTypedDate(raw, this.dateOnly);
    if (!parsed || dateFieldRangeError(parsed, this.dateOnly, this.min, this.max)) {
      return;
    }
    const formatted = formatDateField(parsed, this.dateOnly);
    if (formatted === raw && this.stored) {
      return;
    }
    if ((native.value || '') !== value || native.selectionStart !== value.length) {
      return;
    }
    this.commitDate(parsed);
  }

  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.applyPendingText();
    this.focused = false;
  }

  /**
   * Apply typed text that has not been committed yet.
   * Returns the error currently shown under the field.
   */
  applyPendingText(): string {
    const canonical = this.stored ? formatDateField(this.stored, this.dateOnly) : '';
    if ((this.text || '').trim() !== canonical) {
      this.commitText();
    }
    return this.error;
  }

  togglePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.pickerOpen = !this.pickerOpen;
  }

  onPicked(date: Date): void {
    this.pickerOpen = false;
    this.touched = true;
    this.onTouched();
    this.commitDate(date);
    this.focused = false;
  }

  onDismiss(): void {
    this.pickerOpen = false;
    this.dismissed.emit();
  }

  onClear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    const typed = (this.text || '').trim();
    const canonical = this.stored ? formatDateField(this.stored, this.dateOnly) : '';
    if (this.stored && this.error) {
      this.text = canonical;
      this.error = '';
      this.touched = true;
      this.onTouched();
      this.focused = false;
      this.onValidatorChange();
      return;
    }
    const showingSavedValue = !!this.stored && !this.error && typed === canonical;
    if (!showingSavedValue) {
      this.touched = true;
      this.onTouched();
      this.commitDate(null);
      this.focused = false;
      return;
    }
    if (this.clearRequest.observed) {
      this.clearRequest.emit();
      return;
    }
    this.touched = true;
    this.onTouched();
    this.commitDate(null);
    this.focused = false;
  }

  private commitText(): void {
    const raw = (this.text || '').trim();
    if (!raw) {
      this.commitDate(null);
      return;
    }
    const parsed = parseTypedDate(raw, this.dateOnly);
    if (!parsed) {
      this.fail(dateFieldFormatError(this.dateOnly));
      return;
    }
    const rangeError = dateFieldRangeError(parsed, this.dateOnly, this.min, this.max);
    if (rangeError) {
      this.text = formatDateField(parsed, this.dateOnly);
      this.fail(rangeError);
      return;
    }
    this.commitDate(parsed);
  }

  private commitDate(date: Date | null): void {
    if (!date) {
      this.stored = null;
      this.text = '';
      this.error = this.required && this.touched ? this.requiredMessage : '';
      this.publish(null, null);
      return;
    }
    const rangeError = dateFieldRangeError(date, this.dateOnly, this.min, this.max);
    if (rangeError) {
      this.text = formatDateField(date, this.dateOnly);
      this.fail(rangeError);
      return;
    }
    this.stored = serializeDateField(date, this.dateOnly, this.storage);
    this.text = formatDateField(date, this.dateOnly);
    this.error = '';
    this.publish(this.stored, date);
  }

  private fail(message: string): void {
    this.error = message;
    if (this.stored) {
      this.onValidatorChange();
      return;
    }
    this.publish(null, null);
  }

  private publish(value: string | null, date: Date | null): void {
    this.publishing = true;
    this.onChange(value);
    this.publishing = false;
    this.dateChange.emit(date);
    this.onValidatorChange();
  }
}
